import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 硬核科普与通识教育 — 宇宙尺度之旅
 *
 * 星空 + 对数尺度滑动条 + 每个层级的可视化对比
 * 叙事弧：从夸克 (10⁻¹⁸ m) 一路放大到可观测宇宙 (10²⁶ m)
 *           中途穿插对比物体，让观众建立直观感受
 * 尺寸：1080 × 1920 (竖屏)
 */

interface ScaleStop {
  exponent: number;
  meters: string;
  label: string;
  english: string;
  emoji: string;
  description: string;
  color: string;
}

interface CosmicScaleJourneyProps {
  topic: string;
  subtitle: string;
  hookLine: string;
  stops: ScaleStop[];
  funFact: string;
  bigQuestion: string;
  source: string;
}

const DEFAULT_PROPS: CosmicScaleJourneyProps = {
  topic: '宇宙尺度之旅',
  subtitle: 'A Journey Across 44 Orders of Magnitude',
  hookLine: '从一个夸克到整个可观测宇宙，跨越 44 个数量级——你以为的「大」与「小」，可能完全错了。',
  stops: [
    {
      exponent: -18,
      meters: '0.000000000000000001 m',
      label: '夸克',
      english: 'Quark',
      emoji: '⚛︎',
      description: '物质的最小单位，没有更小的内部结构',
      color: '#a78bfa',
    },
    {
      exponent: -15,
      meters: '0.000000000000001 m',
      label: '原子核',
      english: 'Atomic Nucleus',
      emoji: '◉',
      description: '若把原子放大到足球场，原子核只有一颗芝麻大小',
      color: '#c084fc',
    },
    {
      exponent: -10,
      meters: '0.0000000001 m',
      label: '原子',
      english: 'Atom',
      emoji: '⚭',
      description: '99.99% 都是真空，物质的"实在感"是错觉',
      color: '#e879f9',
    },
    {
      exponent: -9,
      meters: '0.000000001 m',
      label: 'DNA 双螺旋',
      english: 'DNA Helix',
      emoji: '🧬',
      description: '人体一个细胞内的 DNA 拉直 = 2 米',
      color: '#f472b6',
    },
    {
      exponent: -6,
      meters: '0.000001 m',
      label: '红细胞',
      english: 'Red Blood Cell',
      emoji: '🩸',
      description: '直径约 8 μm，每秒诞生 200 万个',
      color: '#fb7185',
    },
    {
      exponent: -3,
      meters: '0.001 m',
      label: '蚂蚁触角',
      english: 'Ant Antenna',
      emoji: '🐜',
      description: '从这里开始，肉眼可见的世界',
      color: '#fda4af',
    },
    {
      exponent: 0,
      meters: '1 m',
      label: '人类',
      english: 'Human',
      emoji: '🧍',
      description: '我们是宇宙尺度的几何中点',
      color: '#fbbf24',
    },
    {
      exponent: 4,
      meters: '10,000 m',
      label: '珠穆朗玛峰',
      english: 'Mt. Everest',
      emoji: '⛰️',
      description: '海拔 8,849 米，地球的最高点',
      color: '#facc15',
    },
    {
      exponent: 7,
      meters: '12,742,000 m',
      label: '地球直径',
      english: 'Earth Diameter',
      emoji: '🌍',
      description: '我们的家——蓝色弹珠',
      color: '#34d399',
    },
    {
      exponent: 9,
      meters: '1,400,000,000 m',
      label: '太阳直径',
      english: 'Sun Diameter',
      emoji: '☀️',
      description: '能装下 130 万个地球',
      color: '#fde047',
    },
    {
      exponent: 12,
      meters: '5,900,000,000,000 m',
      label: '冥王星轨道',
      english: 'Pluto Orbit',
      emoji: '🪐',
      description: '光从太阳到冥王星需 5.5 小时',
      color: '#06b6d4',
    },
    {
      exponent: 16,
      meters: '40,000,000,000,000,000 m',
      label: '比邻星',
      english: 'Proxima Centauri',
      emoji: '✨',
      description: '距太阳最近的恒星，4.24 光年',
      color: '#7dd3fc',
    },
    {
      exponent: 21,
      meters: '1,000,000,000,000,000,000,000 m',
      label: '银河系',
      english: 'Milky Way',
      emoji: '🌌',
      description: '直径约 10 万光年，含 1000-4000 亿恒星',
      color: '#a78bfa',
    },
    {
      exponent: 23,
      meters: '100,000,000,000,000,000,000,000 m',
      label: '本星系群',
      english: 'Local Group',
      emoji: '🌠',
      description: '银河系所属的星系团，含 80 多个星系',
      color: '#c084fc',
    },
    {
      exponent: 26,
      meters: '88,000,000,000,000,000,000,000,000 m',
      label: '可观测宇宙',
      english: 'Observable Universe',
      emoji: '🌀',
      description: '直径约 930 亿光年，包含 2 万亿星系',
      color: '#f0abfc',
    },
  ],
  funFact: '一个氢原子和整个银河系的尺度差，等于一个氢原子和 0.000000001 个银河系的差',
  bigQuestion: '在 44 个数量级的尺度上，你站在哪里？',
  source: 'NASA Cosmic Distance Ladder · CERN Particle Physics',
};

export const CosmicScaleJourneyTemplate: React.FC<
  Partial<CosmicScaleJourneyProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const introEnd = 60;
  const stopDuration = 40;
  const totalStops = p.stops.length;
  const stopsEnd = introEnd + totalStops * stopDuration;
  const summaryStart = stopsEnd + 20;
  const totalFrames = summaryStart + 90;

  // Sliding scale indicator across stops
  const scaleProgress = interpolate(
    frame,
    [introEnd, stopsEnd],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const currentStopFloat = interpolate(
    frame,
    [introEnd, stopsEnd],
    [0, totalStops - 0.001],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );
  const currentStopIndex = Math.floor(currentStopFloat);

  const introOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const introExit = interpolate(frame, [introEnd - 10, introEnd], [1, 0.15], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const summaryOpacity = interpolate(
    frame,
    [summaryStart, summaryStart + 30],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 30%, #1e1b4b 0%, #0a0a1f 50%, #000000 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* ===== Star field background ===== */}
      {Array.from({ length: 200 }).map((_, i) => {
        const x = (i * 173) % 1080;
        const y = (i * 419) % 1920;
        const size = 0.6 + (i % 5) * 0.6;
        const twinkle = 0.2 + 0.7 * Math.abs(Math.sin(frame * 0.04 + i * 0.7));
        return (
          <div
            key={`star-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: i % 17 === 0 ? '#a78bfa' : '#ffffff',
              opacity: twinkle * 0.7,
              boxShadow: i % 11 === 0 ? `0 0 ${size * 6}px currentColor` : undefined,
            }}
          />
        );
      })}

      {/* ===== Intro overlay (fades out) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: introOpacity * introExit,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 20px',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontSize: 36,
            color: '#c4b5fd',
            letterSpacing: '0.2em',
            marginBottom: 18,
          }}
        >
          ◇  COSMIC JOURNEY  ◇
        </div>
        <h1
          style={{
            fontSize: 110,
            fontWeight: 900,
            margin: 0,
            background: 'linear-gradient(135deg, #ffffff 10%, #c4b5fd 50%, #f0abfc 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            lineHeight: 1.1,
            textShadow: '0 0 40px rgba(196,181,253,0.4)',
          }}
        >
          {p.topic}
        </h1>
        <div
          style={{
            fontSize: 40,
            color: '#94a3b8',
            marginTop: 18,
            fontStyle: 'italic',
          }}
        >
          {p.subtitle}
        </div>
        <div
          style={{
            marginTop: 80,
            padding: '24px 32px',
            maxWidth: 900,
            background: 'rgba(167,139,250,0.08)',
            borderLeft: '4px solid #a78bfa',
            borderRadius: '4px 14px 14px 4px',
            fontSize: 36,
            lineHeight: 1.5,
            color: '#e9d5ff',
            textAlign: 'left',
          }}
        >
          {p.hookLine}
        </div>
      </div>

      {/* ===== Header (after intro) ===== */}
      {frame >= introEnd - 20 && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 60,
              left: 60,
              right: 60,
              opacity: interpolate(frame, [introEnd - 5, introEnd + 15], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }),
            }}
          >
            <div
              style={{
                fontSize: 36,
                color: '#c4b5fd',
                letterSpacing: '0.2em',
                marginBottom: 6,
              }}
            >
              ◇ 宇宙尺度之旅 ◇
            </div>
            <h2
              style={{
                fontSize: 56,
                fontWeight: 900,
                margin: 0,
                background: 'linear-gradient(90deg, #ffffff, #c4b5fd, #f0abfc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {p.stops[currentStopIndex]?.label}
            </h2>
            <div style={{ fontSize: 40, color: '#94a3b8', fontStyle: 'italic' }}>
              {p.stops[currentStopIndex]?.english}
            </div>
          </div>

          {/* ===== Logarithmic scale ruler ===== */}
          <div
            style={{
              position: 'absolute',
              top: 280,
              left: 60,
              right: 60,
              height: 100,
              background: 'rgba(15,15,35,0.6)',
              border: '1px solid rgba(167,139,250,0.2)',
              borderRadius: 16,
              padding: '14px 20px',
            }}
          >
            <div
              style={{
                fontSize: 36,
                color: '#94a3b8',
                letterSpacing: '0.1em',
                marginBottom: 10,
              }}
            >
              对数尺度（米 · log₁₀ scale）
            </div>
            <div
              style={{
                position: 'relative',
                height: 40,
                background:
                  'linear-gradient(90deg, #a78bfa, #f472b6 35%, #fbbf24 50%, #34d399 70%, #06b6d4 90%, #f0abfc)',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              {/* Tick marks for each stop */}
              {p.stops.map((s, i) => {
                const x = (i / (totalStops - 1)) * 100;
                return (
                  <div
                    key={`tick-${i}`}
                    style={{
                      position: 'absolute',
                      left: `${x}%`,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: 'rgba(255,255,255,0.4)',
                    }}
                  />
                );
              })}
              {/* Slider */}
              <div
                style={{
                  position: 'absolute',
                  left: `${scaleProgress * 100}%`,
                  top: -8,
                  bottom: -8,
                  width: 4,
                  background: '#fff',
                  boxShadow: '0 0 12px #fff',
                  transform: 'translateX(-50%)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `${scaleProgress * 100}%`,
                  top: -32,
                  transform: 'translateX(-50%)',
                  background: '#fff',
                  color: '#0a0a1f',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontSize: 20,
                  fontWeight: 800,
                  fontFamily: 'monospace',
                }}
              >
                10^{p.stops[currentStopIndex]?.exponent}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#64748b',
                marginTop: 4,
              }}
            >
              <span>10⁻¹⁸ m</span>
              <span>原子</span>
              <span>1 m</span>
              <span>地球</span>
              <span>10²⁶ m</span>
            </div>
          </div>

          {/* ===== Current stop visualization (large center) ===== */}
          {(() => {
            const stop = p.stops[currentStopIndex];
            if (!stop) return null;
            const localFrame = frame - (introEnd + currentStopIndex * stopDuration);
            const stopScale = spring({
              frame: localFrame,
              fps,
              from: 0.4,
              to: 1,
              config: { damping: 16, stiffness: 90 },
            });
            const stopOpacity = interpolate(localFrame, [0, 15], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            return (
              <>
                {/* Big emoji center */}
                <div
                  style={{
                    position: 'absolute',
                    top: 500,
                    left: 60,
                    right: 60,
                    height: 700,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: stopOpacity,
                  }}
                >
                  {/* Outer glow ring */}
                  <div
                    style={{
                      position: 'absolute',
                      width: 580,
                      height: 580,
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${stop.color}25 0%, transparent 70%)`,
                      transform: `scale(${1 + Math.sin(frame * 0.03) * 0.05})`,
                    }}
                  />
                  {/* Concentric circles */}
                  {[0, 1, 2].map((ring) => (
                    <div
                      key={ring}
                      style={{
                        position: 'absolute',
                        width: 280 + ring * 120,
                        height: 280 + ring * 120,
                        borderRadius: '50%',
                        border: `1px solid ${stop.color}${ring === 0 ? '50' : '25'}`,
                        opacity: 0.4 + Math.sin(frame * 0.04 + ring) * 0.15,
                      }}
                    />
                  ))}
                  {/* Big emoji */}
                  <div
                    style={{
                      fontSize: 220,
                      transform: `scale(${stopScale})`,
                      filter: `drop-shadow(0 0 40px ${stop.color}aa)`,
                      lineHeight: 1,
                    }}
                  >
                    {stop.emoji}
                  </div>
                  {/* Meters value */}
                  <div
                    style={{
                      marginTop: 40,
                      padding: '14px 30px',
                      background: 'rgba(15,15,35,0.85)',
                      border: `1.5px solid ${stop.color}55`,
                      borderRadius: 14,
                      fontFamily: 'monospace',
                      fontSize: 40,
                      color: stop.color,
                      fontWeight: 800,
                      maxWidth: 900,
                      textAlign: 'center',
                      wordBreak: 'break-all',
                    }}
                  >
                    {stop.meters}
                  </div>
                  {/* Description */}
                  <div
                    style={{
                      marginTop: 28,
                      padding: '20px 30px',
                      maxWidth: 900,
                      background: `linear-gradient(135deg, ${stop.color}15, transparent)`,
                      border: `1px solid ${stop.color}30`,
                      borderRadius: 16,
                      fontSize: 40,
                      lineHeight: 1.5,
                      color: '#e2e8f0',
                      textAlign: 'center',
                    }}
                  >
                    {stop.description}
                  </div>
                </div>
              </>
            );
          })()}

          {/* ===== Stop counter ===== */}
          <div
            style={{
              position: 'absolute',
              top: 200,
              right: 60,
              padding: '10px 18px',
              background: 'rgba(167,139,250,0.15)',
              border: '1px solid rgba(167,139,250,0.3)',
              borderRadius: 30,
              fontSize: 30,
              fontWeight: 800,
              color: '#c4b5fd',
              fontFamily: 'monospace',
            }}
          >
            STOP {String(currentStopIndex + 1).padStart(2, '0')} / {String(totalStops).padStart(2, '0')}
          </div>

          {/* ===== Mini timeline of all stops (bottom, hidden when summary shows) ===== */}
          <div
            style={{
              position: 'absolute',
              bottom: 60,
              left: 40,
              right: 40,
              padding: '20px 24px',
              background: 'rgba(15,15,35,0.7)',
              border: '1px solid rgba(167,139,250,0.2)',
              borderRadius: 14,
              opacity: interpolate(
                frame,
                [summaryStart - 20, summaryStart],
                [1, 0],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              ),
            }}
          >
            <div
              style={{
                fontSize: 44,
                fontWeight: 700,
                color: '#c4b5fd',
                letterSpacing: '0.08em',
                marginBottom: 18,
              }}
            >
              ◇ 旅程地图
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              {p.stops.map((s, i) => {
                const isPast = i < currentStopIndex;
                const isCurrent = i === currentStopIndex;
                return (
                  <div
                    key={`mini-${i}`}
                    style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      textAlign: 'center',
                      opacity: isPast || isCurrent ? 1 : 0.4,
                      transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
                      transition: 'transform 0.2s',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 56,
                        lineHeight: 1,
                        filter: isCurrent ? `drop-shadow(0 0 8px ${s.color})` : 'none',
                      }}
                    >
                      {s.emoji}
                    </div>
                    <div
                      style={{
                        fontSize: 26,
                        color: isCurrent ? s.color : '#94a3b8',
                        fontFamily: 'monospace',
                        fontWeight: isCurrent ? 900 : 600,
                        marginTop: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      10<sup style={{ fontSize: '0.7em' }}>{s.exponent}</sup>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ===== Summary (fades in to replace mini timeline) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          right: 60,
          padding: '18px 24px',
          background:
            'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(244,114,182,0.12))',
          border: '1.5px solid rgba(167,139,250,0.3)',
          borderRadius: 16,
          opacity: summaryOpacity,
        }}
      >
        <div style={{ fontSize: 18, color: '#94a3b8', letterSpacing: '0.1em', marginBottom: 8 }}>
          ◇ 旅程感悟
        </div>
        <div
          style={{
            fontSize: 22,
            color: '#fbbf24',
            lineHeight: 1.5,
            marginBottom: 12,
            fontWeight: 700,
          }}
        >
          🌌 {p.funFact}
        </div>
        <div
          style={{
            fontSize: 28,
            color: '#fff',
            lineHeight: 1.4,
            fontWeight: 800,
            textShadow: '0 0 12px rgba(196,181,253,0.4)',
          }}
        >
          {p.bigQuestion}
        </div>
        <div style={{ fontSize: 14, color: '#64748b', marginTop: 8 }}>
          数据来源：{p.source}
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, totalFrames], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #a78bfa, #f472b6, #fbbf24, #34d399, #06b6d4, #f0abfc)',
        }}
      />
    </AbsoluteFill>
  );
};
